import { useQuery } from "@tanstack/react-query"
import _ from "lodash"
import { formatQuery } from "react-querybuilder"

import {
  EventsRequests,
  EventsResponses,
} from "@costory/types/endpoints/events"
import {
  ExploreRequests,
  ExploreResponses,
} from "@costory/types/endpoints/explorer"
import {
  VirtualDimensionsRequests,
  VirtualDimensionsResponses,
} from "@costory/types/endpoints/virtualDimensions"
import { Filters } from "@costory/types/filters"

import { apiClient } from "@costory/front/lib/apiClient"

export const useExplorerQuery = (
  filters: Filters,
  isEnabled = true,
  options?: {
    includeForecast?: boolean
  },
) => {
  return useQuery({
    queryKey: [
      "charts",
      "explorer",
      filters.metricId,
      {
        ...filters,
        whereClause: formatQuery(filters.whereClause, "json_without_ids"),
      },
      options?.includeForecast ? "with-forecast" : "no-forecast",
    ],
    queryFn: async () => {
      const response = await apiClient.post<ExploreResponses.ExplorerData>(
        "/explore/explorer",
        {
          filters,
          includeForecast: options?.includeForecast ?? false,
        },
      )
      return response.data
    },
    enabled: isEnabled,
  })
}

export const useExplorerChangeDataPointQuery = (
  filters: Filters | undefined,
  showTrends?: boolean,
) => {
  return useQuery({
    queryKey: [
      "charts",
      "explorer",
      "change-data-point",
      filters?.metricId,
      filters
        ? {
            ...filters,
            whereClause: formatQuery(filters.whereClause, "json_without_ids"),
          }
        : undefined,
    ],
    queryFn: async () => {
      if (!filters) {
        throw new Error("Filters are required for change data point query")
      }
      const response =
        await apiClient.post<ExploreResponses.ExplorerDataWithChangeDataPoint>(
          "/explore/explorer/change-data-point",
          { filters, showTrends },
        )
      return response.data
    },
    enabled: !!filters,
  })
}

export const useEventsQuery = ({
  searchForm,
}: {
  searchForm: EventsRequests.Search
}) => {
  return useQuery({
    queryKey: ["events", "search", searchForm],
    queryFn: async () => {
      const response = await apiClient.post<EventsResponses.Aggregated>(
        "/events/aggregate",
        searchForm,
      )
      return response.data
    },
  })
}

export const useWaterfallQuery = (filters: Filters) => {
  const filtersWithoutNonExplorer = _.omit(filters, ["aggBy"])
  return useQuery({
    queryKey: [
      "charts",
      "waterfall",
      filters.metricId,
      {
        ...filtersWithoutNonExplorer,
        whereClause: formatQuery(
          filtersWithoutNonExplorer.whereClause,
          "json_without_ids",
        ),
      },
    ],
    queryFn: async () => {
      const response = await apiClient.post<
        ExploreRequests.WaterfallData,
        { data: ExploreResponses.Waterfall }
      >("/explore/waterfall", filters)
      return response.data
    },
  })
}

export const useExplorerDrawer = (
  data: VirtualDimensionsRequests.SheetExplorerData,
) => {
  return useQuery({
    queryKey: ["virtual-dimensions", "prefill", data],
    queryFn: async () => {
      const response = await apiClient.post<
        VirtualDimensionsRequests.SheetExplorerData,
        { data: VirtualDimensionsResponses.SheetExplorerData }
      >("/virtual-dimensions/explore-cost", data)
      return response.data
    },
  })
}
